import { queryOptions } from '@tanstack/react-query';

import { BRAND_SERVICE_VERSION_HEADERS } from '@/constants/environments';
import apiClient from '@/constants/http';
import type { Assignment, Service, ServiceCategory } from '@/types/booking';

import type { FetchServicesParams, ServicesResponse } from './types';

/**
 * The barber-scoped endpoint (`/shop/:id/barber/:barberId/service`) returns
 * assignment data as TOP-LEVEL fields (assigneeId, assignmentType, cost,
 * duration) with NO `assignments[]` array — unlike the shop-level endpoint,
 * which carries a populated `assignments[]`. `buildDisplayServices` reads
 * `service.assignments[]` for per-barber cost/duration, so we synthesize a
 * single assignment entry from the top-level fields to keep one uniform shape.
 */
type BarberScopedServiceRaw = Omit<Service, 'assignments'> & {
  assigneeId?: string;
  assignments?: Assignment[];
};

const normalizeBarberScopedService = (raw: BarberScopedServiceRaw): Service => {
  // If the response already carries assignments (defensive: API shape may
  // converge later), leave it untouched.
  if (Array.isArray(raw.assignments) && raw.assignments.length > 0) {
    return raw as Service;
  }

  const assignment: Assignment = {
    order: raw.order ?? 0,
    assigneeId: raw.assigneeId ?? '',
    assignmentId: raw.id,
    assignmentType: raw.assignmentType ?? 'barber',
    parentId: raw.parentId ?? null,
    serviceId: raw.id,
    cost: raw.cost,
    duration: raw.duration,
  };

  return {
    ...raw,
    assignments: [assignment],
  };
};

const buildServicesResponse = (services: Service[]): ServicesResponse => {
  // Transform and filter services, sorted by order to match water-on-web
  const normalized = services
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((service) => ({
      ...service,
      // Ensure required fields have defaults
      duration: service.duration || 0,
      cost: service.cost || 0,
      addonOnly: service.addonOnly || false,
      categories: service.categories || [],
      assignments: service.assignments || [],
    }))
    .filter((service) => {
      // Filter out services without required data
      return service.name && typeof service.cost === 'number';
    });

  // Derive unique categories from services
  const categoryMap = new Map<string, ServiceCategory>();
  normalized.forEach((service) => {
    service.categories?.forEach((category) => {
      if (category.id && category.name && !categoryMap.has(category.id)) {
        categoryMap.set(category.id, { id: category.id, name: category.name });
      }
    });
  });

  const categories = Array.from(categoryMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return { services: normalized, categories };
};

/**
 * Fetches services for a specific shop, or scoped to a specific barber when a
 * real barber id is supplied. The barber-scoped path fixes SQR-14002, where the
 * shop-level endpoint returns no services for booking-code-gated barbers.
 */
const fetchServices = async ({
  shopId,
  barberId,
  include = ['serviceCategories'],
}: FetchServicesParams): Promise<ServicesResponse> => {
  const isSpecificBarber = !!barberId && barberId !== 'any';

  try {
    if (isSpecificBarber) {
      // The barber-scoped endpoint guarantees the barber's services appear (the
      // shop-level one can return [] for them). Normalize the top-level shape
      // into a single synthesized assignment for cost/duration resolution.
      const { data } = await apiClient.get<BarberScopedServiceRaw[]>(
        `/v2/shop/${shopId}/barber/${barberId}/service`,
        {
          params: { sortBy: 'order', include: 'addonServices', limit: '500' },
          headers: BRAND_SERVICE_VERSION_HEADERS,
        },
      );

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: expected array of services');
      }

      return buildServicesResponse(data.map(normalizeBarberScopedService));
    }

    const params: Record<string, string> = {};
    if (include.length > 0) {
      params.include = include.join(',');
    }

    const { data } = await apiClient.get<Service[]>(
      `/v2/shop/${shopId}/service`,
      { params, headers: BRAND_SERVICE_VERSION_HEADERS },
    );

    if (!Array.isArray(data)) {
      throw new Error('Invalid response format: expected array of services');
    }

    return buildServicesResponse(data);
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch services: ${error.message}`);
    }
    throw error;
  }
};

export const getServicesQuery = (
  shopId: string,
  barberId?: string | null,
  include?: string[],
) => {
  const queryKey = ['booking', 'services', shopId, { barberId, include }];

  return queryOptions({
    queryKey,
    queryFn: () => fetchServices({ shopId, barberId, include }),
    retry: false,
    enabled: !!shopId,
    // Same cache window as getProfessionalsQuery and getBarberByIdOrRouteQuery.
    // Without it the list went stale immediately and was refetched on every step
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};
